import { Component, OnInit, Inject, Optional, ViewChild, AfterViewInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';

import { PropertyService } from '../../services/property.service';
import { NavigationContextService } from '../../services/navigation-context.service';
import { MessageRespondComponent } from '../message-respond/message-respond.component';

@Component({
  selector: 'app-admin-all-messages',
  templateUrl: './admin-all-messages.component.html',
  styleUrls: ['./admin-all-messages.component.scss']
})
export class AdminAllMessagesComponent implements OnInit, AfterViewInit {
  property: any;
  loading: boolean = true;
  
  // Table properties
  allDisplayedColumns: string[] = ['property', 'subject', 'from', 'email', 'lastMessage', 'actions'];
  propertyDisplayedColumns: string[] = ['subject', 'from', 'email', 'lastMessage', 'actions'];
  get displayedColumns(): string[] {
    return this.property ? this.propertyDisplayedColumns : this.allDisplayedColumns;
  }
  dataSource = new MatTableDataSource<any>();
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  // Pagination
  totalItems = 0;
  currentPage = 0;
  pageSize = 10;
  pageSizeOptions = [10, 20, 50];

  // Filters
  allProperties: any[] = [];
  selectedPropertyId: string | null = null;
  selectedReadStatus: string | null = null; // 'UNREAD', 'READ', or null
  searchText: string = '';
  selectedDateFilter: string = 'all'; // 'all', 'thisMonth', 'lastMonth', 'custom'
  customStartDate: string = '';
  customEndDate: string = '';
  
  allConversations: any[] = [];
  filteredConversations: any[] = [];

  constructor(@Optional() public dialogRef: MatDialogRef<AdminAllMessagesComponent>,
              @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
              private propertyService: PropertyService,
              private route: ActivatedRoute,
              private router: Router,
              private dialog: MatDialog,
              private navContext: NavigationContextService) { }

  async ngOnInit() {
    this.loading = true;
    
    if (this.data && this.data.property) {
      this.property = this.data.property;
      this.loadConversations();
    } else {
      const propertyId = this.route.snapshot.queryParamMap.get('propertyId');
      if (propertyId) {
        try {
          this.property = await this.propertyService.getPublicProperty(propertyId);
          this.loadConversations();
        } catch (error) {
          console.error('Error loading property:', error);
          this.property = null;
          this.loadAllConversations();
        }
      } else {
        // No property specified - show all messages for organization
        this.property = null;
        this.loadAllConversations();
      }
    }
  }

  ngAfterViewInit() {
    if (this.sort && this.dataSource) {
      this.dataSource.sort = this.sort;
    }
    // Explicitly ensure paginator is NOT connected to dataSource for server-side pagination
    if (this.dataSource.paginator) {
      this.dataSource.paginator = null;
    }
  }

  loadAllConversations() {
    this.loading = true;
    Promise.all([
      this.propertyService.getConversations(null, this.selectedReadStatus || undefined),
      this.propertyService.getProperties()
    ]).then(([conversations, properties]: [any, any[]]) => {
      this.loading = false;
      // Handle response - could be array or object
      let conversationsArray = [];
      if (Array.isArray(conversations)) {
        conversationsArray = conversations;
      } else if (conversations && conversations.data && Array.isArray(conversations.data)) {
        conversationsArray = conversations.data;
      }
      
      this.allConversations = conversationsArray;
      this.allProperties = Array.isArray(properties) ? properties : [];
      this.totalItems = this.allConversations.length;
      console.log('Loaded conversations:', this.allConversations.length);
      this.applyAllFilters();
    }).catch((error) => {
      console.error('Error loading conversations:', error);
      this.loading = false;
      this.allConversations = [];
      this.allProperties = [];
      this.totalItems = 0;
      this.updateDataSource();
    });
  }

  loadConversations() {
    if (this.property && this.property.id) {
      this.loading = true;
      this.propertyService.getConversations(this.property, this.selectedReadStatus || undefined).then((conversations: any) => {
        this.loading = false;
        // Handle response - could be array or object
        let conversationsArray = [];
        if (Array.isArray(conversations)) {
          conversationsArray = conversations;
        } else if (conversations && conversations.data && Array.isArray(conversations.data)) {
          conversationsArray = conversations.data;
        }
        this.allConversations = conversationsArray;
        console.log('Loaded property conversations:', this.allConversations.length);
        this.applyFilters();
      }).catch((error) => {
        console.error('Error loading conversations:', error);
        this.loading = false;
        this.allConversations = [];
        this.applyFilters();
      });
    } else {
      this.loadAllConversations();
    }
  }

  applyFilter(event: Event) {
    this.searchText = (event.target as HTMLInputElement).value;
    if (this.property) {
      this.applyFilters();
    } else {
      this.applyAllFilters();
    }
  }

  applyAllFilters() {
    // For all conversations view, apply client-side filters
    let filtered = [...this.allConversations];

    // Property filter (client-side on already loaded data)
    if (this.selectedPropertyId) {
      filtered = filtered.filter(conv => {
        return conv.property && conv.property.id === this.selectedPropertyId;
      });
    }

    // Search filter
    if (this.searchText && this.searchText.trim()) {
      const searchLower = this.searchText.toLowerCase().trim();
      filtered = filtered.filter(conv => {
        const subject = (conv.subject || '').toLowerCase();
        const name = (conv.name || '').toLowerCase();
        const email = (conv.email || '').toLowerCase();
        const messageText = this.getAllMessagesText(conv).toLowerCase();
        return subject.includes(searchLower) || 
               name.includes(searchLower) || 
               email.includes(searchLower) ||
               messageText.includes(searchLower);
      });
    }

    // Date filter
    if (this.selectedDateFilter !== 'all') {
      const now = new Date();
      let startDate: Date | null = null;
      let endDate: Date | null = null;

      if (this.selectedDateFilter === 'thisMonth') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      } else if (this.selectedDateFilter === 'lastMonth') {
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
      } else if (this.selectedDateFilter === 'custom' && this.customStartDate && this.customEndDate) {
        startDate = new Date(this.customStartDate);
        endDate = new Date(this.customEndDate);
        endDate.setHours(23, 59, 59);
      }

      if (startDate && endDate) {
        filtered = filtered.filter(conv => {
          const convDate = new Date(conv.updated_on || conv.updatedOn || conv.created_on || conv.createdOn);
          return convDate >= startDate && convDate <= endDate;
        });
      }
    }

    this.filteredConversations = filtered;
    this.totalItems = filtered.length;
    this.currentPage = 0;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.updateDataSource();
  }

  applyFilters() {
    // For property-specific view
    let filtered = [...this.allConversations];

    // Search filter
    if (this.searchText && this.searchText.trim()) {
      const searchLower = this.searchText.toLowerCase().trim();
      filtered = filtered.filter(conv => {
        const subject = (conv.subject || '').toLowerCase();
        const name = (conv.name || '').toLowerCase();
        const email = (conv.email || '').toLowerCase();
        const messageText = this.getAllMessagesText(conv).toLowerCase();
        return subject.includes(searchLower) || 
               name.includes(searchLower) || 
               email.includes(searchLower) ||
               messageText.includes(searchLower);
      });
    }

    this.filteredConversations = filtered;
    this.totalItems = filtered.length;
    this.currentPage = 0;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.updateDataSource();
  }

  onPropertyFilterChange(propertyId: string | null) {
    this.selectedPropertyId = propertyId;
    this.applyAllFilters();
  }

  onReadStatusFilterChange(readStatus: string | null) {
    this.selectedReadStatus = readStatus;
    this.currentPage = 0;
    this.loadAllConversations();
  }

  onDateFilterChange(filter: string) {
    this.selectedDateFilter = filter;
    if (filter !== 'custom') {
      this.customStartDate = '';
      this.customEndDate = '';
    }
    this.applyAllFilters();
  }

  onCustomDateChange() {
    this.applyAllFilters();
  }

  clearFilters() {
    this.selectedPropertyId = null;
    this.selectedReadStatus = null;
    this.searchText = '';
    this.selectedDateFilter = 'all';
    this.customStartDate = '';
    this.customEndDate = '';
    if (this.property) {
      this.loadConversations();
    } else {
      this.currentPage = 0;
      this.loadAllConversations();
    }
  }

  onPageChange(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updateDataSource();
  }

  onRowClick(event: MouseEvent, conversation: any) {
    // Prevent row click from triggering when clicking on action buttons
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('a')) {
      return;
    }
    // View conversation when row is clicked
    this.viewConversation(conversation);
  }

  viewConversation(conversation: any) {
    // Get property for the conversation
    const propertyId = this.property ? this.property.id : (conversation.property ? conversation.property.id : null);
    
    if (propertyId) {
      // Load property and open message dialog
      this.propertyService.getPublicProperty(propertyId).then(property => {
        const dialogRef = this.dialog.open(MessageRespondComponent, {
          width: '800px',
          maxWidth: '100vw',
          maxHeight: '100vh',
          panelClass: 'mobile-dialog-panel',
          autoFocus: false,
          data: { 
            property: property,
            conversation: conversation
          }
        });

        dialogRef.afterClosed().subscribe(() => {
          // Refresh conversations after dialog closes
          if (this.property) {
            this.loadConversations();
          } else {
            this.loadAllConversations();
          }
        });
      }).catch(error => {
        console.error('Error loading property for conversation:', error);
      });
    } else {
      // If no property, navigate to message-respond route
      this.router.navigate(['message-respond'], { queryParams: { conversationId: conversation.id } });
    }
  }

  hasUnreadMessages(conversation: any): boolean {
    if (!conversation.messages || !Array.isArray(conversation.messages)) {
      return false;
    }
    // Check if any message is unread (assuming unread status)
    return conversation.messages.some((msg: any) => {
      return msg.readStatus === 'UNREAD' || !msg.readStatus;
    });
  }

  getLastMessageDate(conversation: any): Date | null {
    if (!conversation.messages || !Array.isArray(conversation.messages) || conversation.messages.length === 0) {
      return conversation.updated_on || conversation.updatedOn || conversation.created_on || conversation.createdOn || null;
    }
    const lastMessage = conversation.messages[conversation.messages.length - 1];
    return lastMessage.date || lastMessage.created_on || lastMessage.createdOn || null;
  }

  getLastMessagePreview(conversation: any): string {
    if (!conversation.messages || !Array.isArray(conversation.messages) || conversation.messages.length === 0) {
      return 'No messages';
    }
    const lastMessage = conversation.messages[conversation.messages.length - 1];
    const preview = lastMessage.message || '';
    return preview.length > 100 ? preview.substring(0, 100) + '...' : preview;
  }

  getAllMessagesText(conversation: any): string {
    if (!conversation.messages || !Array.isArray(conversation.messages)) {
      return '';
    }
    return conversation.messages.map((msg: any) => msg.message || '').join(' ');
  }

  private updateDataSource() {
    if (this.property) {
      // Property-specific: client-side pagination
      const startIndex = this.currentPage * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      const paginatedData = this.filteredConversations.slice(startIndex, endIndex);
      this.dataSource.data = paginatedData;
    } else {
      // All conversations: client-side pagination on filtered results
      const startIndex = this.currentPage * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      const paginatedData = this.filteredConversations.slice(startIndex, endIndex);
      this.dataSource.data = paginatedData;
    }
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  goToAdminLanding() {
    this.navContext.goToDashboard();
  }

  close() {
    if (this.dialogRef) {
      this.dialogRef.close();
      return;
    }
    if (this.property && this.property.id) {
      this.navContext.goToPropertyHub(this.property.id);
      return;
    }
    this.navContext.goToDashboard();
  }
}
